import { toast as baseToast } from "@/components/ui/use-toast";
import type { ToastActionElement } from "@/components/ui/toast";

export interface ToastOptions {
  title?: string;
  description?: string;
  duration?: number;
  action?: ToastActionElement;
}

/**
 * Standardized toast helpers for consistent user feedback across the app.
 * Uses consistent styling, icons via CSS/Tailwind, and standard durations.
 */

/**
 * Show a success toast (green)
 * Default duration: 3000ms (3 seconds)
 */
export function showSuccess(message: string, options?: ToastOptions) {
  return baseToast({
    title: options?.title || "✓ Success",
    description: message,
    duration: options?.duration || 3000,
    action: options?.action,
    className: "border-emerald-200 bg-emerald-50 text-emerald-900 [&>div]:text-emerald-700",
  });
}

/**
 * Show an error toast (red)
 * Default duration: 5000ms (5 seconds)
 */
export function showError(message: string, options?: ToastOptions) {
  return baseToast({
    title: options?.title || "✕ Error",
    description: message,
    duration: options?.duration || 5000,
    action: options?.action,
    className: "border-red-200 bg-red-50 text-red-900 [&>div]:text-red-700",
    variant: "destructive",
  });
}

/**
 * Show a warning toast (yellow/amber)
 * Default duration: 4000ms (4 seconds)
 */
export function showWarning(message: string, options?: ToastOptions) {
  return baseToast({
    title: options?.title || "⚠ Warning",
    description: message,
    duration: options?.duration || 4000,
    action: options?.action,
    className: "border-amber-200 bg-amber-50 text-amber-900 [&>div]:text-amber-700",
  });
}

/**
 * Show an info toast (blue)
 * Default duration: 3000ms (3 seconds)
 */
export function showInfo(message: string, options?: ToastOptions) {
  return baseToast({
    title: options?.title || "ℹ Info",
    description: message,
    duration: options?.duration || 3000,
    action: options?.action,
    className: "border-blue-200 bg-blue-50 text-blue-900 [&>div]:text-blue-700",
  });
}

/**
 * Convenience method for showing action result toasts
 * Automatically shows success or error based on result
 */
export function showActionResult(
  success: boolean,
  successMessage: string,
  errorMessage: string,
  options?: ToastOptions
) {
  if (success) {
    return showSuccess(successMessage, options);
  } else {
    return showError(errorMessage, options);
  }
}

/**
 * Show a loading toast (can be updated later)
 * Returns methods to update or dismiss the toast
 * Duration is set to Infinity (won't auto-dismiss)
 */
export function showLoading(message: string, options?: ToastOptions) {
  const loadingToast = baseToast({
    title: options?.title || "⏳ Loading",
    description: message,
    duration: Infinity, // Don't auto-dismiss
    className: "border-cyan-200 bg-cyan-50 text-cyan-900 [&>div]:text-cyan-700",
  });

  return {
    ...loadingToast,
    /**
     * Update the loading toast to show success
     */
    success: (successMessage: string, successOptions?: ToastOptions) => {
      loadingToast.dismiss();
      return showSuccess(successMessage, successOptions);
    },
    /**
     * Update the loading toast to show error
     */
    error: (errorMessage: string, errorOptions?: ToastOptions) => {
      loadingToast.dismiss();
      return showError(errorMessage, errorOptions);
    },
  };
}
